// ./src/components/features/TemplateCategories/TemplateContentBlockPickerModal/TemplateContentBlockPickerModal.tsx
import React, { useState, useMemo } from "react";
import {
  Stack,
  Group,
  Text,
  SimpleGrid,
  Paper,
  Box,
  ScrollArea,
  useMantineTheme,
  NavLink,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { AppModal } from "@/components/generic/AppModal";
import { TemplateCategorySelector } from "../TemplateCategorySelector";
import { useTemplateCategoryItems } from "@/hooks/useTemplateCategoryItems";
import { useTemplateCategories } from "@/hooks/useTemplateCategories";
import { useApp } from "@/contexts/AppContext";
import { ContentBlock } from "@/utils/api/ContentBlockService";
import { LoadingIndicator } from "@/components/generic/LoadingIndicator";
import { omit } from "@/utils";

export interface TemplateContentBlockPickerModalProps {
  /** Whether the modal is open */
  open: boolean;

  /** Callback when modal is closed */
  onClose: () => void;

  /** Callback when a content block is selected */
  onChange: (contentBlock: ContentBlock) => void;

  /** Modal title */
  title?: string;
}

export const TemplateContentBlockPickerModal: React.FC<
  TemplateContentBlockPickerModalProps
> = ({ open, onClose, onChange, title = "Select Template" }) => {
  const theme = useMantineTheme();
  const { isAuthenticated } = useApp();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  const [selectedContentBlock, setSelectedContentBlock] =
    useState<ContentBlock | null>(null);

  const {
    templateCategories,
    loading: categoriesLoading,
    list: listCategories,
  } = useTemplateCategories({
    autoLoad: false,
    isAuthenticated,
  });

  const { templateCategoryItems, loading, isEmpty, list } =
    useTemplateCategoryItems({
      autoLoad: false,
      isAuthenticated,
    });

  // Load categories when modal opens (only if not already loaded)
  React.useEffect(() => {
    if (open && templateCategories.length === 0 && !categoriesLoading) {
      listCategories({}, undefined, undefined, true);
    }
  }, [open, templateCategories.length, categoriesLoading, listCategories]);

  // Load items when category changes
  React.useEffect(() => {
    if (selectedCategoryId) {
      // Reset selection when category changes
      setSelectedContentBlock(null);
      // Load items for the selected category
      list(
        { templateCategoryId: [selectedCategoryId] },
        undefined,
        undefined,
        true
      );
    }
  }, [selectedCategoryId, list]);

  // Auto-select first category when categories load
  React.useEffect(() => {
    if (
      !categoriesLoading &&
      templateCategories.length > 0 &&
      !selectedCategoryId
    ) {
      setSelectedCategoryId(templateCategories[0].id || null);
    }
  }, [categoriesLoading, templateCategories, selectedCategoryId]);

  // Items are already filtered by the API call
  const filteredItems = useMemo(() => {
    return templateCategoryItems;
  }, [templateCategoryItems]);

  // Handle content block selection
  const handleContentBlockClick = (contentBlock: ContentBlock) => {
    setSelectedContentBlock(contentBlock);
  };

  // Handle select button click
  const handleSelect = () => {
    if (selectedContentBlock) {
      // Create a copy with nullified id
      const blockCopy = {
        ...omit(selectedContentBlock, ["__typename"]),
        id: "",
      };
      onChange(blockCopy);
      onClose();
    }
  };

  // Handle modal close
  const handleClose = () => {
    setSelectedContentBlock(null);
    onClose();
  };

  // Render content block preview
  const renderContentBlockPreview = (contentBlock: ContentBlock) => {
    const contentType = contentBlock.contentType?.toUpperCase();

    if (contentType === "HTML" && contentBlock.contentData) {
      return (
        <Box
          style={{
            width: "100%",
            height: 150,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            style={{
              width: "500%",
              height: 750,
              pointerEvents: "none",
              transform: "scale(0.2)",
              transformOrigin: "top left",
            }}
            dangerouslySetInnerHTML={{ __html: contentBlock.contentData }}
          />
        </Box>
      );
    }

    if (contentType === "IMAGE" && contentBlock.fileUrl) {
      return (
        <img
          src={contentBlock.fileUrl}
          alt={contentBlock.name || "Template"}
          style={{
            width: "100%",
            height: 150,
            objectFit: "cover",
          }}
        />
      );
    }

    return (
      <Box
        style={{
          width: "100%",
          height: 150,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: theme.colors.gray[1],
        }}
      >
        <Text c="dimmed" size="sm">
          Preview not available
        </Text>
      </Box>
    );
  };

  // Render content block tile
  const renderContentBlockTile = (item: any) => {
    const contentBlock = item.contentBlock;
    if (!contentBlock) return null;

    const isSelected = selectedContentBlock?.id === contentBlock.id;

    return (
      <Paper
        key={item.id}
        shadow="sm"
        p="md"
        withBorder
        style={{
          cursor: "pointer",
          borderColor: isSelected ? theme.colors.blue[6] : undefined,
          borderWidth: isSelected ? 2 : 1,
        }}
        onClick={() => handleContentBlockClick(contentBlock)}
      >
        <Stack gap="xs">
          {renderContentBlockPreview(contentBlock)}
          <Text fw={500} size="sm" lineClamp={1}>
            {contentBlock.name || "Unnamed Template"}
          </Text>
          {contentBlock.description && (
            <Text size="xs" c="dimmed" lineClamp={2}>
              {contentBlock.description}
            </Text>
          )}
        </Stack>
      </Paper>
    );
  };

  // Desktop layout
  const renderDesktopLayout = () => (
    <Group
      align="flex-start"
      gap="md"
      style={{
        height: "calc(95vh - 260px)", // Fixed height accounting for modal header/footer
        overflow: "hidden",
        display: "flex",
        alignItems: "stretch",
      }}
    >
      {/* Left side - Category list */}
      <Box
        style={{
          width: 250,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Text fw={600} size="lg" mb="md">
          Categories
        </Text>
        <ScrollArea style={{ flex: 1 }} type="auto">
          {categoriesLoading && (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: theme.spacing.md,
              }}
            >
              <LoadingIndicator size="sm" />
            </Box>
          )}
          {!categoriesLoading && templateCategories.length === 0 && (
            <Text c="dimmed" size="sm" ta="center">
              No categories available
            </Text>
          )}
          {!categoriesLoading && templateCategories.length > 0 && (
            <Stack gap={0}>
              {templateCategories.map((category) => {
                const isSelected = selectedCategoryId === category.id;
                return (
                  <NavLink
                    key={category.id}
                    label={category.name || "Unnamed Category"}
                    active={isSelected}
                    onClick={() => setSelectedCategoryId(category.id || null)}
                  />
                );
              })}
            </Stack>
          )}
        </ScrollArea>
      </Box>

      {/* Right side - Content blocks */}
      <Box
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <ScrollArea style={{ flex: 1 }} type="auto">
          {loading && (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
              }}
            >
              <LoadingIndicator size="lg" />
            </Box>
          )}

          {!loading && isEmpty && selectedCategoryId && (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
              }}
            >
              <Text c="dimmed">No templates available in this category</Text>
            </Box>
          )}

          {!loading &&
            !isEmpty &&
            filteredItems.length === 0 &&
            selectedCategoryId && (
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 400,
                }}
              >
                <Text c="dimmed">No templates found</Text>
              </Box>
            )}

          {!loading && filteredItems.length > 0 && (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
              {filteredItems.map(renderContentBlockTile)}
            </SimpleGrid>
          )}
        </ScrollArea>
      </Box>
    </Group>
  );

  // Mobile layout
  const renderMobileLayout = () => (
    <Stack
      gap="md"
      style={{
        minHeight: "70vh",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TemplateCategorySelector
        value={selectedCategoryId}
        onChange={(categoryId) => {
          setSelectedCategoryId(categoryId);
          setSelectedContentBlock(null);
        }}
        placeholder="Select category"
        isAuthenticated={isAuthenticated}
        required
      />

      <ScrollArea style={{ flex: 1 }} type="auto">
        {loading && (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
            }}
          >
            <LoadingIndicator size="lg" />
          </Box>
        )}

        {!loading && isEmpty && selectedCategoryId && (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
            }}
          >
            <Text c="dimmed">No templates available in this category</Text>
          </Box>
        )}

        {!loading &&
          !isEmpty &&
          filteredItems.length === 0 &&
          selectedCategoryId && (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
              }}
            >
              <Text c="dimmed">No templates found</Text>
            </Box>
          )}

        {!loading && filteredItems.length > 0 && (
          <SimpleGrid cols={1} spacing="md">
            {filteredItems.map(renderContentBlockTile)}
          </SimpleGrid>
        )}
      </ScrollArea>
    </Stack>
  );

  return (
    <AppModal
      opened={open}
      onClose={handleClose}
      title={title}
      size="95%"
      closable={true}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleClose,
        },
        {
          label: "Select",
          variant: "primary",
          onClick: handleSelect,
          disabled: !selectedContentBlock,
        },
      ]}
    >
      {isMobile ? renderMobileLayout() : renderDesktopLayout()}
    </AppModal>
  );
};
